import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/Skeleton";
import { toast } from "sonner";
import Markdown from "@/components/Markdown";
import { ArrowLeft, MessageSquare, Loader2, Sparkles, Copy, Check } from "lucide-react";

const INSTITUTION_TYPES = ["School", "International School", "University", "College", "Activity Center", "Coaching", "Preschool"];
const CURRICULUMS = ["CBSE", "ICSE", "State Board", "IB", "IGCSE / Cambridge", "A-Levels", "AP", "US Common Core", "UK National Curriculum", "Other"];
const COUNTRIES = ["India", "UAE", "United States", "United Kingdom", "Singapore", "Canada", "Australia", "Other"];

export default function GrowthInterviewPrep() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    role: "Mathematics Teacher",
    institution_type: "School",
    curriculum: "CBSE",
    country: "India",
    duration_min: 30,
    notes: "",
  });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const generate = async () => {
    if (!form.role?.trim()) { toast.error("Role is required"); return; }
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post("/growth/interview-prep", form);
      setResult(data);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.error === "INSUFFICIENT_COINS") {
        toast.error(`Need ${detail.required} coins · You have ${detail.balance}`, {
          action: { label: "Recharge", onClick: () => navigate("/wallet") },
        });
      } else {
        toast.error(typeof detail === "string" ? detail : "Could not generate. Try again.");
      }
    } finally { setBusy(false); }
  };

  const copy = async () => {
    if (!result?.content) return;
    try { await navigator.clipboard.writeText(result.content); setCopied(true); toast.success("Pack copied"); setTimeout(() => setCopied(false), 2000); } catch { toast.error("Copy failed"); }
  };

  return (
    <Layout title="Interview Prep">
      <Button variant="ghost" onClick={() => navigate("/growth-studio")} className="mb-3 -ml-2" data-testid="interview-back">
        <ArrowLeft className="h-4 w-4 mr-1.5" /> Back to Growth Studio
      </Button>

      <div className="mb-6">
        <span className="tb-chip-sky inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold tracking-wide">
          <MessageSquare className="h-3 w-3" /> Interview Preparation
        </span>
        <h1 className="mt-3 font-display text-3xl sm:text-4xl tracking-tight font-medium leading-tight">
          Walk into your interview <span className="text-primary">prepared.</span>
        </h1>
        <p className="mt-1.5 text-sm text-muted-foreground max-w-2xl">
          Self-introduction, 12 anticipated questions with model answers, behavioural STAR scenarios,
          and 5 smart questions to ask the panel — all tailored to your role and country.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        <div className="lg:col-span-5 tb-card p-5 self-start lg:sticky lg:top-6" data-testid="interview-form">
          <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-muted-foreground">Inputs</p>

          <div className="mt-4 space-y-3">
            <Field label="Role you're interviewing for">
              <Input value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="h-10 rounded-xl" data-testid="interview-role" />
            </Field>
            <Field label="Institution">
              <Select value={form.institution_type} options={INSTITUTION_TYPES} onChange={(v) => setForm({ ...form, institution_type: v })} testid="interview-inst" />
            </Field>
            <Field label="Curriculum">
              <Select value={form.curriculum} options={CURRICULUMS} onChange={(v) => setForm({ ...form, curriculum: v })} testid="interview-curr" />
            </Field>
            <Field label="Country">
              <Select value={form.country} options={COUNTRIES} onChange={(v) => setForm({ ...form, country: v })} testid="interview-country" />
            </Field>
            <Field label="Expected duration (min)">
              <Input
                type="number" min="10" max="120"
                value={form.duration_min}
                onChange={(e) => setForm({ ...form, duration_min: parseInt(e.target.value || "30", 10) })}
                className="h-10 rounded-xl"
                data-testid="interview-duration"
              />
            </Field>
            <Field label="Notes (e.g. focus on STEM teaching, robotics, leadership)">
              <Textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Any specific themes the panel might ask about"
                className="rounded-xl min-h-[88px]"
                data-testid="interview-notes"
              />
            </Field>

            <div className="flex items-center justify-between rounded-xl bg-muted/40 px-3 py-2 border border-border">
              <p className="text-xs">Exact cost: <span className="font-medium">3 coins</span></p>
              <span className="inline-flex items-center gap-1 text-[10px] font-mono px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-500/10 dark:text-amber-200">3 🪙</span>
            </div>

            <Button onClick={generate} disabled={busy} className="w-full h-11 rounded-full" data-testid="interview-generate">
              {busy ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5 mr-1.5" />}
              {busy ? "Drafting prep pack…" : "Generate interview pack"}
            </Button>
          </div>
        </div>

        <div className="lg:col-span-7" data-testid="interview-result">
          {busy && <Skeleton className="h-72" />}
          {!busy && !result && (
            <div className="tb-card p-8 text-center">
              <MessageSquare className="h-8 w-8 mx-auto text-muted-foreground" />
              <p className="mt-3 text-sm text-muted-foreground">Configure on the left, then your full interview pack appears here.</p>
            </div>
          )}
          {result && (
            <div className="tb-card p-5 tb-fade-up">
              <div className="flex items-center justify-between mb-3">
                <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-muted-foreground">Interview prep · {form.role}</p>
                <Button variant="ghost" size="sm" onClick={copy} className="h-7 px-2 text-xs">
                  {copied ? <Check className="h-3 w-3 mr-1 text-emerald-500" /> : <Copy className="h-3 w-3 mr-1" />}
                  {copied ? "Copied" : "Copy"}
                </Button>
              </div>
              <div className="prose prose-sm dark:prose-invert max-w-none">
                <Markdown>{result.content}</Markdown>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <p className="text-[11px] font-medium mb-1.5">{label}</p>
      {children}
    </div>
  );
}

function Select({ value, options, onChange, testid }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full h-10 rounded-xl border border-border bg-card px-3 text-sm"
      data-testid={testid}
    >
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}
